import { ChangeEvent, FC, useState } from "react";
import { useDispatch } from "react-redux";

import { changeSaveProgressSettings } from "./../../../store/settings/actionCreators";
import { selectFormSaveProgressSettings } from "./../../../store/settings/selectors";
import { IEmailEditorData } from "../../../store/settings/types";
import useShallowSelector from "./../../../hooks/useShalowSelector";
import { IconTypes } from "./../../../libs/Icon";
import Switch from "./../../../libs/Switch";
import Button from "./../../../libs/Button";
import SaveProgressTextEditor from "./../Notification/RespondentNotification/NotificationTextEditor";
import styles from "./styles.module.scss";

const SaveProgress: FC = () => {
    const dispatch = useDispatch();
    const saveProgressSettings = useShallowSelector(selectFormSaveProgressSettings);
    const [, setSaveAndResume] = useState<boolean>(saveProgressSettings.saveAndResume);
    const [, setStoreSubmission] = useState<boolean>(saveProgressSettings.storeSubmission.isActive);
    const [emailSubject, setEmailSubject] = useState<string>(saveProgressSettings.allowThePerson.emailSubject);
    const [message, setMessage] = useState<IEmailEditorData>({
        html: saveProgressSettings.allowThePerson.message,
        json: saveProgressSettings.allowThePerson.json,
    });
    const [loadButtonLabel, setLoadButtonLabel] = useState<string>(
        saveProgressSettings.storeSubmission.loadButtonLabel,
    );
    const [saveButtonLabel, setSaveButtonLabel] = useState<string>(
        saveProgressSettings.storeSubmission.saveButtonLabel,
    );

    const getEmailSubject = (e: ChangeEvent<HTMLInputElement>) => setEmailSubject(e.target.value);
    const getLoadButton = (e: ChangeEvent<HTMLInputElement>) => setLoadButtonLabel(e.target.value);
    const getSaveButton = (e: ChangeEvent<HTMLInputElement>) => setSaveButtonLabel(e.target.value);

    const handleStateUpdate = (element: any) => {
        dispatch(
            changeSaveProgressSettings({
                prevState: saveProgressSettings,
                currentState: {
                    ...element,
                },
            }),
        );
    };

    return (
        <>
            <div className={styles.section}>
                <label className={styles.switchContainer}>
                    <Switch
                        checked={saveProgressSettings.saveAndResume}
                        onChange={() => {
                            setSaveAndResume(state => !state);
                            handleStateUpdate({
                                ...saveProgressSettings,
                                saveAndResume: !saveProgressSettings.saveAndResume,
                            });
                        }}
                    />

                    <p className={styles.subTitle}>Reset & Resume</p>
                </label>

                {saveProgressSettings?.saveAndResume && (
                    <>
                        <div className={styles.checkBoxBlock}>
                            <Button
                                iconSize={20}
                                style={{ color: saveProgressSettings.allowThePerson.isActive ? "#1DCFA9" : "#434E5A" }}
                                icon={
                                    saveProgressSettings.allowThePerson.isActive
                                        ? IconTypes.CHECKBOX
                                        : IconTypes.INACTIVE_CHECKBOX
                                }
                                onClick={() => {
                                    handleStateUpdate({
                                        ...saveProgressSettings,
                                        allowThePerson: {
                                            ...saveProgressSettings.allowThePerson,
                                            isActive: !saveProgressSettings.allowThePerson.isActive,
                                        },
                                    });
                                }}
                            />
                            <span>Allow the person who fills your form receive saved form’s link via E-mail</span>
                        </div>

                        {saveProgressSettings?.allowThePerson.isActive && (
                            <>
                                <p className={styles.subTitle}>E-Mail Subject</p>

                                <input
                                    type="text"
                                    value={emailSubject}
                                    onChange={e => getEmailSubject(e)}
                                    className={styles.customInput}
                                    onBlur={() =>
                                        handleStateUpdate({
                                            ...saveProgressSettings,
                                            allowThePerson: {
                                                ...saveProgressSettings.allowThePerson,
                                                emailSubject: emailSubject,
                                            },
                                        })
                                    }
                                />

                                <p className={styles.subTitle}>Message</p>

                                <SaveProgressTextEditor
                                    invoiceInitial={message}
                                    handleChange={e => {
                                        setMessage(e);
                                        handleStateUpdate({
                                            ...saveProgressSettings,
                                            allowThePerson: {
                                                ...saveProgressSettings.allowThePerson,
                                                message: e,
                                            },
                                        });
                                    }}
                                />
                            </>
                        )}
                    </>
                )}
            </div>

            <div className={styles.line} />

            <div className={styles.section}>
                <label className={styles.switchContainer}>
                    <Switch
                        checked={saveProgressSettings.storeSubmission.isActive}
                        onChange={() => {
                            setStoreSubmission(state => !state);
                            handleStateUpdate({
                                ...saveProgressSettings,
                                storeSubmission: {
                                    ...saveProgressSettings.storeSubmission,
                                    isActive: !saveProgressSettings.storeSubmission.isActive,
                                },
                            });
                        }}
                    />

                    <p className={styles.subTitle}>Store Submission</p>
                </label>

                {saveProgressSettings.storeSubmission.isActive && (
                    <>
                        <p className={styles.label}>
                            The submitted form remains in the database, so the user can either submit same info again or
                            click to reset and start filling again.
                        </p>

                        <div className={styles.buttonLabels}>
                            <div>
                                <p className={styles.subTitle}>Load Button Label</p>

                                <input
                                    type="text"
                                    value={loadButtonLabel}
                                    onChange={e => getLoadButton(e)}
                                    className={styles.customInput}
                                    onBlur={() =>
                                        handleStateUpdate({
                                            ...saveProgressSettings,
                                            storeSubmission: {
                                                ...saveProgressSettings.storeSubmission,

                                                loadButtonLabel: loadButtonLabel,
                                            },
                                        })
                                    }
                                />
                            </div>

                            <div>
                                <p className={styles.subTitle}>Reset Button Label</p>

                                <input
                                    type="text"
                                    value={saveButtonLabel}
                                    onChange={e => getSaveButton(e)}
                                    className={styles.customInput}
                                    onBlur={() =>
                                        handleStateUpdate({
                                            ...saveProgressSettings,
                                            storeSubmission: {
                                                ...saveProgressSettings.storeSubmission,
                                                saveButtonLabel: saveButtonLabel,
                                            },
                                        })
                                    }
                                />
                            </div>
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default SaveProgress;
